import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Ban,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Search,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { deliveryHref } from "../../deliveries";
import { clockOf } from "../../dispatch";
import { messageFor } from "../../error-copy";
import {
  addDays,
  type Choice,
  dayCounts,
  dayLong,
  dayMonth,
  dayShort,
  driversFor,
  FIT,
  initialChoices,
  litres,
  moves,
  NEEDS,
  type PlanRow,
  pickable,
  poolOf,
  problemOf,
  shortLitres,
  siteUse,
  suggested,
  trucksOfDay,
  type WeekPlan,
  weekday,
  weekPlanQuery,
  weekRange,
} from "../../plan";
import styles from "./plan.module.css";

type View = "deliveries" | "fillsites";
/** The Day select's value for "still in the pool". */
const POOL = "pool";

/**
 * Plan the week (P8-74, Operations): for each delivery without a truck, a suggested fill site, truck
 * and driver to review and assign in bulk, days ahead; each day against the fleet's truck loads,
 * and each fill site's expected litres against its daily limit.
 */
export function PlanPage(): ReactNode {
  const [week, setWeek] = useState<string | undefined>(undefined);
  const plan = useQuery(weekPlanQuery(week));
  const header = (description?: string, actions?: ReactNode) => (
    <PageHeader
      title="Plan the week"
      description={description ?? "Assign a week of deliveries at once, days ahead."}
      actions={actions}
    />
  );
  if (plan.status === "pending")
    return (
      <>
        {header()}
        <PageLoading label="Working out the week" />
      </>
    );
  if (plan.status === "error")
    return (
      <>
        {header()}
        <PageError message={messageFor(errorOf(plan.error))} onRetry={() => plan.refetch()} />
      </>
    );
  return <Planner key={plan.data.from} plan={plan.data} setWeek={setWeek} header={header} />;
}

function Planner({
  plan,
  setWeek,
  header,
}: {
  plan: WeekPlan;
  setWeek: (day: string | undefined) => void;
  header: (description?: string, actions?: ReactNode) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const [choices, setChoices] = useState(() => initialChoices(plan));
  // a fresh plan (after assigning) starts again from its suggestions
  useEffect(() => setChoices(initialChoices(plan)), [plan]);
  // the first day with deliveries on it, else today
  const [day, setDay] = useState(
    plan.plan_days.find((d) => plan.rows.some((r) => !r.flexible && r.suggestion.date === d)) ??
      plan.plan_days[0] ??
      plan.from,
  );
  const [notify, setNotify] = useState(false);
  const [view, setView] = useState<View>("deliveries");
  const [q, setQ] = useState("");

  const inSelection = (r: PlanRow) => choices[r.id]?.date === day;
  const rowsOfDay = plan.rows.filter(
    (r) =>
      inSelection(r) &&
      (!q.trim() ||
        [r.delivery_number, r.customer, r.location?.label, r.order.order_number]
          .filter(Boolean)
          .some((s) => (s as string).toLowerCase().includes(q.trim().toLowerCase()))),
  );
  const ready = (r: PlanRow) => {
    const c = choices[r.id];
    return Boolean(c?.selected && problemOf(plan, r, c) === null);
  };
  const chosen = plan.rows.filter((r) => inSelection(r) && ready(r));
  const assign = useMutation({
    mutationFn: () =>
      ok(
        api.POST("/v1/admin/dispatch/plan/assign", {
          body: {
            assignments: chosen.map((r) => {
              const c = choices[r.id] as Choice;
              return {
                delivery_id: r.id,
                fillsite_id: c.fillsite_id as string,
                truck_id: c.truck_id as string,
                driver_id: c.driver_id as string,
                date: c.date ?? undefined,
              };
            }),
            notify_customer: notify,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dispatch-plan"] });
      await queryClient.invalidateQueries({ queryKey: ["dispatch-board"] });
    },
  });
  const set = (id: string, patch: Partial<Choice>) => {
    assign.reset();
    setChoices((all) => ({ ...all, [id]: { ...(all[id] as Choice), ...patch } }));
  };

  // every row: the week's, and the ones from earlier days spread over it
  const toPlan = plan.rows.length;
  const earlier = plan.overdue.count;
  const pooled = poolOf(plan, choices).length;
  const loadsADay = plan.trucks.reduce((sum, t) => sum + t.loads_per_day, 0);
  const thisWeek = plan.today >= plan.from && plan.today <= plan.to;
  return (
    <>
      {header(
        `${weekRange(plan.from, plan.to)} · ${toPlan} ${toPlan === 1 ? "delivery" : "deliveries"} without a truck${pooled ? `, ${pooled} in the pool` : ""}${earlier ? ` (${earlier} from earlier days)` : ""} · ${plan.trucks.length} trucks, ${loadsADay} loads a day`,
        <Link to="/dispatch" search={{}}>
          <Button variant="secondary">Back to Dispatch</Button>
        </Link>,
      )}
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.week}>
            <button
              type="button"
              className={styles.step}
              aria-label="Previous week"
              onClick={() => setWeek(addDays(plan.from, -7))}
            >
              <ChevronLeft size={16} />
            </button>
            <span className={styles.weekLabel}>
              <CalendarRange size={16} aria-hidden="true" />
              {thisWeek ? "This week, " : ""}
              {weekRange(plan.from, plan.to)}
            </span>
            <button
              type="button"
              className={styles.step}
              aria-label="Next week"
              onClick={() => setWeek(addDays(plan.from, 7))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
          {thisWeek ? null : (
            <Button variant="ghost" onClick={() => setWeek(undefined)}>
              This week
            </Button>
          )}
          <label className={styles.search}>
            <Search size={16} aria-hidden="true" />
            <input
              aria-label="Search the day's deliveries"
              placeholder="Delivery, customer or place"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </div>
        <SegmentedControl
          label="View"
          value={view}
          onValueChange={(v) => setView(v as View)}
          options={[
            { value: "deliveries", label: "Deliveries" },
            { value: "fillsites", label: "Fill sites" },
          ]}
        />
      </div>
      <div className={styles.days} role="tablist" aria-label="Days">
        {plan.days.map((server) => {
          const d = { date: server.date, ...dayCounts(plan, choices, server.date) };
          const over = d.truck_loads > d.capacity_loads;
          const pct = d.capacity_loads ? Math.round((d.truck_loads / d.capacity_loads) * 100) : 0;
          return (
            <button
              key={d.date}
              type="button"
              role="tab"
              aria-selected={d.date === day}
              className={styles.day}
              data-selected={d.date === day || undefined}
              data-past={d.date < plan.today || undefined}
              onClick={() => setDay(d.date)}
            >
              <span className={styles.dayTop}>
                <span className={styles.dayName}>
                  {d.date === plan.today ? "Today, " : ""}
                  {dayShort(d.date)}
                </span>
                {over ? (
                  <span className={styles.over}>
                    <TriangleAlert size={12} aria-hidden="true" /> Over
                  </span>
                ) : d.deliveries > 0 && d.without_truck === 0 ? (
                  <CircleCheck size={14} className={styles.done} aria-label="All planned" />
                ) : null}
              </span>
              <span className={styles.count}>
                <strong>{d.deliveries ? d.without_truck : "—"}</strong>
                {d.deliveries ? <span>of {d.deliveries}</span> : null}
              </span>
              <span className={styles.muted}>
                {!d.deliveries
                  ? "no deliveries"
                  : d.without_truck === 0
                    ? "all planned"
                    : `${d.without_truck} to plan${d.overdue ? `, ${d.overdue} overdue` : ""} · ${d.suggested} suggested`}
              </span>
              <span className={styles.bar}>
                <span data-over={over || undefined} style={{ width: `${Math.min(pct, 100)}%` }} />
              </span>
              <span className={over ? styles.overText : styles.muted}>
                {d.truck_loads} of {d.capacity_loads} truck loads
              </span>
            </button>
          );
        })}
      </div>
      {view === "deliveries" ? (
        <div className={styles.columns}>
          <div className={styles.stack}>
            <Pool
              plan={plan}
              day={day}
              choices={choices}
              addAll={(ids) => {
                assign.reset();
                setChoices((all) => {
                  const next = { ...all };
                  for (const id of ids) next[id] = { ...(next[id] as Choice), date: day };
                  return next;
                });
              }}
            />
            <DayTable
              plan={plan}
              day={day}
              rows={rowsOfDay}
              choices={choices}
              set={set}
              selectAll={() =>
                setChoices((all) => {
                  const next = { ...all };
                  for (const r of rowsOfDay)
                    if (problemOf(plan, r, all[r.id] as Choice) === null)
                      next[r.id] = { ...(next[r.id] as Choice), selected: true };
                  return next;
                })
              }
            />
          </div>
          <DayPlan
            plan={plan}
            day={day}
            rows={plan.rows.filter(inSelection)}
            notify={notify}
            setNotify={setNotify}
            choices={choices}
            chosen={chosen}
            assign={() => assign.mutate()}
            pending={assign.isPending}
            result={assign.data}
            error={assign.error ? messageFor(errorOf(assign.error)) : null}
          />
        </div>
      ) : (
        <div className={styles.columns}>
          <SiteGrid plan={plan} choices={choices} day={day} setDay={setDay} />
          <SiteDay plan={plan} choices={choices} day={day} openDeliveries={() => setView("deliveries")} />
        </div>
      )}
    </>
  );
}

function DayTable({
  plan,
  day,
  rows,
  choices,
  set,
  selectAll,
}: {
  plan: WeekPlan;
  day: string;
  rows: PlanRow[];
  choices: Record<string, Choice>;
  set: (id: string, patch: Partial<Choice>) => void;
  selectAll: () => void;
}) {
  const { timezone } = useCompanyFormat();
  const need = rows.filter((r) => !suggested(r.suggestion.fit)).length;
  const label = dayLong(day);
  const earlier = rows.filter((r) => r.overdue).length;
  return (
    <section className={styles.panel} aria-label={`Deliveries: ${label}`}>
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.title}>{label}</h2>
          <p className={styles.muted}>
            {rows.length} without a truck · {rows.length - need} suggested
            {need ? ` · ${need} need a choice` : ""}
            {earlier ? ` · ${earlier} from earlier days` : ""}
          </p>
        </div>
        {rows.length ? (
          <button type="button" className={styles.link} onClick={selectAll}>
            Select all that can go
          </button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className={styles.empty}>
          {day < plan.today
            ? "A past day: what wasn't planned then is spread over the days ahead."
            : "Nothing to plan this day: every delivery has a truck."}
        </p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th aria-label="Selected" />
              <th>Delivery</th>
              <th>Customer · place</th>
              <th className={styles.num}>Litres</th>
              <th>Day &amp; fill site</th>
              <th>Truck &amp; driver</th>
              <th>Fit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const c = choices[r.id] as Choice;
              const problem = problemOf(plan, r, c);
              const truck = plan.trucks.find((t) => t.id === c.truck_id);
              const fit = FIT[r.suggestion.fit];
              const changed =
                c.fillsite_id !== r.suggestion.fillsite_id ||
                c.truck_id !== r.suggestion.truck_id ||
                c.driver_id !== r.suggestion.driver_id;
              return (
                <tr key={r.id} data-off={!c.selected || undefined}>
                  <td>
                    <Checkbox
                      aria-label={`Assign ${r.delivery_number}`}
                      checked={c.selected && problem === null}
                      disabled={problem !== null}
                      onCheckedChange={(on) => set(r.id, { selected: on === true })}
                    />
                  </td>
                  <td>
                    <Link to={deliveryHref(r.order.id, r.id)} className={styles.number}>
                      {r.delivery_number}
                    </Link>
                    <span className={r.overdue ? styles.overText : styles.muted}>
                      {r.overdue ? `${dayMonth(r.date)} · ` : ""}
                      {r.scheduled_at ? clockOf(r.scheduled_at, timezone) : "Any time"}
                    </span>
                  </td>
                  <td>
                    <span className={styles.strong}>{r.customer}</span>
                    <span className={styles.muted}>
                      {[r.location?.label, r.zone?.name].filter(Boolean).join(" · ") || "No place"}
                    </span>
                  </td>
                  <td className={styles.num}>{litres(r.litres)}</td>
                  <td className={styles.crew}>
                    <Select
                      label="Day"
                      value={c.date ?? POOL}
                      onValueChange={(v) => set(r.id, { date: v === POOL ? null : v })}
                      options={[
                        ...plan.plan_days.map((d) => ({
                          value: d,
                          label: d === plan.today ? `Today, ${dayShort(d)}` : dayShort(d),
                        })),
                        ...(r.flexible ? [{ value: POOL, label: "Back to the pool" }] : []),
                      ]}
                    />
                    <Select
                      label="Fill site"
                      placeholder="Choose"
                      value={c.fillsite_id ?? undefined}
                      onValueChange={(v) => set(r.id, { fillsite_id: v })}
                      options={plan.fillsites.map((f) => ({ value: f.id, label: f.name }))}
                    />
                    {moves(r, c) ? (
                      <span className={styles.moves}>
                        {r.overdue || r.scheduled_at ? `Moves from ${dayMonth(r.date)}` : "Gets this day"}
                      </span>
                    ) : null}
                  </td>
                  <td className={styles.crew}>
                    <Select
                      label="Truck"
                      placeholder="Choose"
                      value={c.truck_id ?? undefined}
                      onValueChange={(v) => {
                        const next = plan.trucks.find((t) => t.id === v);
                        const keep = driversFor(plan, next).some((d) => d.id === c.driver_id);
                        set(r.id, { truck_id: v, driver_id: keep ? c.driver_id : (next?.usual_driver_id ?? null) });
                      }}
                      options={plan.trucks.map((t) => ({
                        value: t.id,
                        label: `${t.plate_number} · ${shortLitres(t.capacity_litres)}${t.partner ? ` · ${t.partner.name}` : ""}`,
                        disabled: t.capacity_litres < r.litres,
                      }))}
                    />
                    <Select
                      label="Driver"
                      placeholder="Choose"
                      value={c.driver_id ?? undefined}
                      onValueChange={(v) => set(r.id, { driver_id: v })}
                      options={driversFor(plan, truck).map((d) => ({ value: d.id, label: d.name }))}
                    />
                  </td>
                  <td>
                    {problem && problem !== fit.label && !NEEDS[r.suggestion.fit] ? (
                      <span className={styles.pill} data-tone="warning">
                        {problem}
                      </span>
                    ) : (
                      <span className={styles.pill} data-tone={changed && !problem ? "neutral" : fit.tone}>
                        {r.suggestion.fit === "too_big" ? <Ban size={12} aria-hidden="true" /> : null}
                        {changed && !problem ? "Your choice" : fit.label}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function DayPlan({
  plan,
  day,
  rows,
  notify,
  setNotify,
  choices,
  chosen,
  assign,
  pending,
  result,
  error,
}: {
  plan: WeekPlan;
  day: string;
  rows: PlanRow[];
  notify: boolean;
  setNotify: (on: boolean) => void;
  choices: Record<string, Choice>;
  chosen: PlanRow[];
  assign: () => void;
  pending: boolean;
  result: { assigned: number; results: { delivery_id: string; error: { message: string } | null }[] } | undefined;
  error: string | null;
}) {
  const trucks = trucksOfDay(plan, choices, day);
  const total = chosen.reduce((sum, r) => sum + r.litres, 0);
  const needs = rows.filter((r) => NEEDS[r.suggestion.fit]);
  const moving = chosen.filter((r) => moves(r, choices[r.id])).length;
  const refused = result?.results.filter((r) => r.error) ?? [];
  return (
    <aside className={styles.side} aria-label="The day's plan">
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.title}>{day === plan.today ? "Today's plan" : `${weekday(day)}'s plan`}</h2>
          <p className={styles.muted}>
            {chosen.length} of {rows.length} selected · {litres(total)} · {trucks.length}{" "}
            {trucks.length === 1 ? "truck" : "trucks"}
          </p>
        </div>
      </div>
      {trucks.length ? (
        <div className={styles.section}>
          <p className={styles.label}>By truck</p>
          {trucks.map(({ truck, loads, litres: l, driver }) => (
            <div key={truck.id} className={styles.truck}>
              <div className={styles.truckTop}>
                <span>
                  <span className={styles.strong}>{truck.plate_number}</span>
                  <span className={styles.muted}>{driver ?? truck.partner?.name ?? "Already planned"}</span>
                </span>
                <span className={loads > truck.loads_per_day ? styles.overText : styles.right}>
                  {loads} of {truck.loads_per_day} loads · {litres(l)}
                </span>
              </div>
              <span className={styles.bar}>
                <span
                  data-over={loads > truck.loads_per_day || undefined}
                  style={{ width: `${Math.min(100, Math.round((loads / truck.loads_per_day) * 100))}%` }}
                />
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {needs.length ? (
        <div className={styles.section}>
          <p className={styles.label}>Needs a choice · {needs.length}</p>
          {needs.map((r) => (
            <div key={r.id} className={styles.need}>
              {r.suggestion.fit === "too_big" ? (
                <Ban size={16} className={styles.danger} aria-hidden="true" />
              ) : (
                <TriangleAlert size={16} className={styles.warning} aria-hidden="true" />
              )}
              <span>
                <span className={styles.strong}>
                  {r.delivery_number} · {litres(r.litres)}
                </span>
                <span className={styles.muted}>{NEEDS[r.suggestion.fit]}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <div className={styles.footer}>
        {error ? (
          <Alert tone="danger" title="Not assigned">
            {error}
          </Alert>
        ) : null}
        {result ? (
          <Alert tone={refused.length ? "warning" : "success"} title={`${result.assigned} assigned`}>
            {refused.length
              ? refused
                  .map(
                    (r) =>
                      `${plan.rows.find((x) => x.id === r.delivery_id)?.delivery_number ?? "A delivery"}: ${r.error?.message}`,
                  )
                  .join(" ")
              : "They're under Assigned on Dispatch for their day."}
          </Alert>
        ) : null}
        {moving ? (
          <Checkbox
            label={`Text ${moving === 1 ? "the customer" : `the ${moving} customers`} the new day`}
            description={`${moving} of these ${moving === 1 ? "moves" : "move"} to ${weekday(day)}: assigning reschedules ${moving === 1 ? "it" : "them"}.`}
            checked={notify}
            onCheckedChange={(on) => setNotify(on === true)}
          />
        ) : null}
        <Button onClick={assign} disabled={!chosen.length} loading={pending}>
          Assign {chosen.length} {chosen.length === 1 ? "delivery" : "deliveries"}
        </Button>
        <p className={styles.muted}>
          Each moves to Assigned on Dispatch for its day, with its fill site, truck and driver. The driver sees it once
          their shift starts that day.
        </p>
      </div>
    </aside>
  );
}

function SiteGrid({
  plan,
  choices,
  day,
  setDay,
}: {
  plan: WeekPlan;
  choices: Record<string, Choice>;
  day: string;
  setDay: (day: string) => void;
}) {
  const use = siteUse(plan, choices);
  const days = plan.days.map((d) => d.date);
  const limitOf = plan.fillsites.reduce((sum, f) => sum + (f.daily_limit_litres ?? 0), 0);
  const unlimited = plan.fillsites.some((f) => f.daily_limit_litres === null);
  return (
    <section className={styles.panel} aria-label="Fill sites, expected this week">
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.title}>Fill sites, expected this week</h2>
          <p className={styles.muted}>Litres drawn by planned and selected loads, against each site's daily limit</p>
        </div>
        <div className={styles.legend}>
          <span data-key="planned">Planned</span>
          <span data-key="chosen">Selected</span>
          <span data-key="over">Over the limit</span>
        </div>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Fill site</th>
            {days.map((d) => (
              <th key={d} data-selected={d === day || undefined}>
                <button type="button" className={styles.colHead} onClick={() => setDay(d)}>
                  {dayShort(d)}
                </button>
              </th>
            ))}
            <th className={styles.num}>Week</th>
          </tr>
        </thead>
        <tbody>
          {plan.fillsites.map((f) => {
            const week = days.reduce((sum, d) => sum + use(f.id, d).planned + use(f.id, d).chosen, 0);
            return (
              <tr key={f.id}>
                <td>
                  <span className={styles.strong}>{f.name}</span>
                  <span className={styles.muted}>
                    {f.partner ? `${f.partner.name}'s` : "Own"} ·{" "}
                    {f.daily_limit_litres ? `limit ${litres(f.daily_limit_litres)} a day` : "no daily limit"}
                  </span>
                </td>
                {days.map((d) => {
                  const u = use(f.id, d);
                  const all = u.planned + u.chosen;
                  const limit = f.daily_limit_litres;
                  const over = limit !== null && all > limit;
                  const of = (l: number) => (limit ? `${Math.min(100, (l / limit) * 100)}%` : all ? "100%" : "0%");
                  return (
                    <td
                      key={d}
                      className={styles.cell}
                      data-over={over || undefined}
                      data-selected={d === day || undefined}
                    >
                      {all ? (
                        <>
                          <span className={styles.cellTop}>
                            <strong>{shortLitres(all)}</strong>
                            {limit ? <span>{Math.round((all / limit) * 100)}%</span> : null}
                          </span>
                          <span className={styles.bar} data-split="">
                            <span data-over={over || undefined} style={{ width: of(u.planned) }} />
                            {over ? null : <span data-chosen="" style={{ width: of(u.chosen) }} />}
                          </span>
                          <span className={styles.muted}>
                            {u.loads} {u.loads === 1 ? "load" : "loads"}
                          </span>
                        </>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                  );
                })}
                <td className={styles.num}>{litres(week)}</td>
              </tr>
            );
          })}
          <tr className={styles.total}>
            <td>
              <span className={styles.strong}>All fill sites</span>
              <span className={styles.muted}>{unlimited ? "Some without a limit" : `${litres(limitOf)} a day`}</span>
            </td>
            {days.map((d) => {
              const all = plan.fillsites.reduce((sum, f) => sum + use(f.id, d).planned + use(f.id, d).chosen, 0);
              const over = !unlimited && all > limitOf;
              return (
                <td key={d} className={over ? styles.overText : undefined}>
                  <strong>{all ? shortLitres(all) : "—"}</strong>
                </td>
              );
            })}
            <td className={styles.num}>
              <strong>
                {litres(
                  days.reduce(
                    (sum, d) =>
                      sum + plan.fillsites.reduce((s, f) => s + use(f.id, d).planned + use(f.id, d).chosen, 0),
                    0,
                  ),
                )}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      <p className={styles.note}>
        Suggestions use the nearest fill site with room. Set a site's daily limit on its page under Fleet › Fill sites.
      </p>
    </section>
  );
}

function SiteDay({
  plan,
  choices,
  day,
  openDeliveries,
}: {
  plan: WeekPlan;
  choices: Record<string, Choice>;
  day: string;
  openDeliveries: () => void;
}) {
  const use = useMemo(() => siteUse(plan, choices), [plan, choices]);
  const sites = plan.fillsites.map((f) => {
    const u = use(f.id, day);
    const all = u.planned + u.chosen;
    return { site: f, ...u, all, room: f.daily_limit_litres === null ? null : f.daily_limit_litres - all };
  });
  const over = sites.filter((s) => s.room !== null && s.room < 0);
  const room = sites.filter((s) => s.room === null || s.room > 0);
  const expected = sites.reduce((sum, s) => sum + s.all, 0);
  return (
    <aside className={styles.side} aria-label={`Fill sites on ${dayLong(day)}`}>
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.title}>{dayLong(day)}</h2>
          <p className={over.length ? styles.overText : styles.muted}>
            {litres(expected)} expected{over.length ? ` · ${over.length} over the limit` : ""}
          </p>
        </div>
      </div>
      {over.length ? (
        <div className={styles.section}>
          <p className={styles.label}>Over the limit</p>
          {over.map((s) => (
            <div key={s.site.id} className={styles.need}>
              <TriangleAlert size={16} className={styles.warning} aria-hidden="true" />
              <span>
                <span className={styles.strong}>{s.site.name}</span>
                <span className={styles.muted}>
                  {litres(s.all)} of {litres(s.site.daily_limit_litres ?? 0)} · {s.loads} loads
                </span>
              </span>
              <span className={styles.overText}>+{litres(-(s.room ?? 0))}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className={styles.section}>
        <p className={styles.label}>Room on {dayShort(day)}</p>
        {room.length ? (
          room.map((s) => (
            <div key={s.site.id} className={styles.truckTop}>
              <span>
                <span className={styles.strong}>{s.site.name}</span>
                <span className={styles.muted}>{s.site.partner ? `${s.site.partner.name}'s` : "Own"}</span>
              </span>
              <span className={styles.free}>{s.room === null ? "No limit" : `${litres(s.room)} free`}</span>
            </div>
          ))
        ) : (
          <p className={styles.muted}>Every fill site is at its limit.</p>
        )}
      </div>
      <div className={styles.footer}>
        <Button variant="secondary" onClick={openDeliveries}>
          Plan {weekday(day)}'s deliveries <ArrowRight size={16} aria-hidden="true" />
        </Button>
        <p className={styles.muted}>Change a delivery's fill site there to move its load to a site with room.</p>
      </div>
    </aside>
  );
}

/**
 * The pool: deliveries from earlier days and for any time, not yet on a day. It can be long, so
 * the page shows how many wait and staff choose the ones for the selected day in a dialog.
 */
function Pool({
  plan,
  day,
  choices,
  addAll,
}: {
  plan: WeekPlan;
  day: string;
  choices: Record<string, Choice>;
  addAll: (ids: string[]) => void;
}) {
  const pool = poolOf(plan, choices);
  const canPick = plan.plan_days.includes(day);
  const [open, setOpen] = useState(false);
  if (!pool.length) return null;
  const overdue = pool.filter((r) => r.overdue).length;
  return (
    <section className={styles.poolBar} aria-label="The pool">
      <span>
        <span className={styles.strong}>The pool · {pool.length} waiting for a day</span>
        <span className={styles.muted}>
          {overdue
            ? `${overdue} from earlier days, since ${dayMonth(pool[0]?.date ?? plan.today)}`
            : "All for any time"}
          {pool.length - overdue && overdue ? ` · ${pool.length - overdue} for any time` : ""}
        </span>
      </span>
      <Button variant="secondary" disabled={!canPick} onClick={() => setOpen(true)}>
        {canPick ? `Choose for ${weekday(day)}…` : "Pick a day from today on"}
      </Button>
      {open ? (
        <PoolPicker
          plan={plan}
          day={day}
          choices={choices}
          pool={pool}
          onClose={() => setOpen(false)}
          onAdd={(ids) => {
            addAll(ids);
            setOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

/** The most rows the picker draws at once; search and filters narrow the rest. */
const PICKER_ROWS = 200;
type Kind = "all" | "overdue" | "any_time";

/** Choose deliveries from the pool for a day: search, filter, tick, and add them. */
function PoolPicker({
  plan,
  day,
  choices,
  pool,
  onClose,
  onAdd,
}: {
  plan: WeekPlan;
  day: string;
  choices: Record<string, Choice>;
  pool: PlanRow[];
  onClose: () => void;
  onAdd: (ids: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<Kind>("all");
  const [zone, setZone] = useState("all");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const zones = [...new Map(pool.flatMap((r) => (r.zone ? [[r.zone.id, r.zone.name] as const] : []))).entries()].sort(
    (a, b) => a[1].localeCompare(b[1]),
  );
  const term = q.trim().toLowerCase();
  const filtered = pool.filter(
    (r) =>
      (kind === "all" || (kind === "overdue" ? r.overdue : !r.overdue)) &&
      (zone === "all" || (zone === "none" ? !r.zone : r.zone?.id === zone)) &&
      (!term ||
        [r.delivery_number, r.customer, r.location?.label, r.order.order_number, r.zone?.name]
          .filter(Boolean)
          .some((t) => (t as string).toLowerCase().includes(term))),
  );
  const shown = filtered.slice(0, PICKER_ROWS);
  const canTake = filtered.filter(pickable);
  const allTicked = canTake.length > 0 && canTake.every((r) => picked.has(r.id));
  const counts = dayCounts(plan, choices, day);
  const room = Math.max(0, counts.capacity_loads - counts.truck_loads);
  const toggle = (id: string, on: boolean) =>
    setPicked((now) => {
      const next = new Set(now);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={styles.picker}
        title={`Choose deliveries for ${dayLong(day)}`}
        description={`${pool.length} in the pool, oldest first. ${weekday(day)} has ${counts.truck_loads} of ${counts.capacity_loads} truck loads planned.`}
      >
        <div className={styles.pickerTools}>
          <label className={styles.search}>
            <Search size={16} aria-hidden="true" />
            <input
              aria-label="Search the pool"
              placeholder="Delivery, order, customer, place or zone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <SegmentedControl
            label="Which"
            value={kind}
            onValueChange={(v) => setKind(v as Kind)}
            options={[
              { value: "all", label: "All" },
              { value: "overdue", label: "From earlier days" },
              { value: "any_time", label: "For any time" },
            ]}
          />
          <div className={styles.pickerZone}>
            <Select
              label="Zone"
              value={zone}
              onValueChange={setZone}
              options={[
                { value: "all", label: "All zones" },
                ...zones.map(([id, name]) => ({ value: id, label: name })),
                { value: "none", label: "No zone" },
              ]}
            />
          </div>
        </div>
        <div className={styles.pickerList}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <Checkbox
                    aria-label="Tick all that can go"
                    checked={allTicked}
                    disabled={!canTake.length}
                    onCheckedChange={(on) =>
                      setPicked((now) => {
                        const next = new Set(now);
                        for (const r of canTake) {
                          if (on === true) next.add(r.id);
                          else next.delete(r.id);
                        }
                        return next;
                      })
                    }
                  />
                </th>
                <th>Delivery</th>
                <th>Customer · place</th>
                <th className={styles.num}>Litres</th>
                <th>Suggested</th>
                <th>Fit</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const fit = FIT[r.suggestion.fit];
                const ok = pickable(r);
                return (
                  <tr key={r.id} data-off={!ok || undefined}>
                    <td>
                      <Checkbox
                        aria-label={`Choose ${r.delivery_number}`}
                        checked={picked.has(r.id)}
                        disabled={!ok}
                        onCheckedChange={(on) => toggle(r.id, on === true)}
                      />
                    </td>
                    <td>
                      <span className={styles.numberText}>{r.delivery_number}</span>
                      <span className={r.overdue ? styles.overText : styles.muted}>
                        {r.overdue ? `Since ${dayMonth(r.date)}` : "Any time"}
                      </span>
                    </td>
                    <td>
                      <span className={styles.strong}>{r.customer}</span>
                      <span className={styles.muted}>
                        {[r.location?.label, r.zone?.name].filter(Boolean).join(" · ") || "No place"}
                      </span>
                    </td>
                    <td className={styles.num}>{litres(r.litres)}</td>
                    <td>
                      <span className={styles.muted}>{dayShort(r.suggestion.date)}</span>
                    </td>
                    <td>
                      <span className={styles.pill} data-tone={fit.tone}>
                        {fit.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 ? <p className={styles.empty}>Nothing in the pool matches.</p> : null}
          {filtered.length > PICKER_ROWS ? (
            <p className={styles.note}>
              Showing the oldest {PICKER_ROWS} of {filtered.length}: search or filter to find the rest. Tick all ticks
              every one that matches.
            </p>
          ) : null}
        </div>
        <div className={styles.pickerFooter}>
          <span className={picked.size > room ? styles.overText : styles.muted}>
            {picked.size} chosen · room for about {room} more {room === 1 ? "load" : "loads"} on {weekday(day)}
          </span>
          <Button
            variant="ghost"
            disabled={!canTake.length || room === 0}
            onClick={() => setPicked(new Set(canTake.slice(0, room).map((r) => r.id)))}
          >
            Choose the oldest {Math.min(room, canTake.length) || ""} to fill it
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!picked.size} onClick={() => onAdd([...picked])}>
            Add {picked.size} to {weekday(day)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
